import _get from 'lodash/get';

import { removeNulls } from '..';
import { getDepthOfDirectedTreeNode } from '.';

interface AddDirectedTreeIconsInput {
    directedTree: ProDirectedTreeNode | null;
    config: SummarizeDirectedTreeConfig;
}

/**
 * Adds .href properties with image URLs for icons.
 * @param {AddDirectedTreeIconsInput} input
 * @returns {ProDirectedTreeNode[] | null}
 */
export const addDirectedTreeIcons = ({
    config,
    directedTree,
}: AddDirectedTreeIconsInput): ProDirectedTreeNode[] | null => {
    if (directedTree === null) {
        return null;
    }

    const { iconMap } = config;

    // Fall back to trackIcon if entityType is not found in entityTypeToIconMap
    const getIconHref = (entityType: string): string =>
        _get(iconMap, [entityType], iconMap.track);

    /**
     * Augment nodes with `href` properties.
     */
    const mapper = (node: ProDirectedTreeNode): DirectedTreeNodeWithHref => {
        const href = getIconHref(node?.entity_type ?? 'none__');

        // If there are no children, add the href and set children to empty array.
        // Intermediate nodes with children === null will break the math that amCharts uses to add
        // up the totals.
        if (node.children === null || node.children === undefined) {
            return {
                ...node,
                href,
                children: [],
            };
        }

        // If the node is not a leaf node, recurse
        if (getDepthOfDirectedTreeNode(node) > 0) {
            return {
                ...node,
                href,
                children: removeNulls(node.children).map(mapper),
            };
        }

        // Base case: the node is a leaf node; just add the href property and return
        return {
            ...node,
            href,
        };
    };

    const nodeList = [directedTree].map(mapper);

    return nodeList;
};
